// Visible export phases. Percentage is shown only when the worker has
// written a real progress value — never invented on the client.

export const QUEUED_STALL_MS = 3 * 60 * 1000;
export const PROCESSING_STALL_MS = 20 * 60 * 1000;

export type ExportPhase = 'preparing' | 'queued' | 'rendering' | 'complete' | 'failed' | 'stalled';

export interface RenderClock {
  status: string;
  queued_at?: string | null;
  created_at: string;
  started_at?: string | null;
  progress?: number | null;
  error_message?: string | null;
}

export function phaseForRender(render: RenderClock, now: number): ExportPhase {
  if (render.status === 'completed') return 'complete';
  if (render.status === 'failed') return 'failed';
  const queuedAt = Date.parse(render.queued_at || render.created_at);
  const startedAt = render.started_at ? Date.parse(render.started_at) : NaN;
  if (render.status === 'queued') {
    if (Number.isFinite(queuedAt) && now - queuedAt > QUEUED_STALL_MS) return 'stalled';
    return 'queued';
  }
  if (render.status === 'processing') {
    const anchor = Number.isFinite(startedAt) ? startedAt : queuedAt;
    if (Number.isFinite(anchor) && now - anchor > PROCESSING_STALL_MS) return 'stalled';
    return 'rendering';
  }
  return 'failed';
}

export function phaseLabel(phase: ExportPhase): string {
  switch (phase) {
    case 'preparing':
      return 'Preparing';
    case 'queued':
      return 'Queued';
    case 'rendering':
      return 'Rendering';
    case 'complete':
      return 'Complete';
    case 'failed':
    case 'stalled':
      return 'Failed';
  }
}

/** Null when the backend has not reported meaningful progress. */
export function meaningfulProgress(render: RenderClock, phase: ExportPhase): number | null {
  if (phase !== 'rendering') return null;
  const value = render.progress ?? 0;
  if (value <= 0 || value >= 100) return null;
  return value;
}
